import React, { useState, useEffect, useCallback } from 'react';
import {
  Heart,
  Info,
  FileText,
  Camera,
  Star,
  Shield,
  Clock,
  Download,
  Share2,
  X,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  Eye,
  Copy,
  Dog,
  MessageSquare
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogClose } from '@/components/ui/dialog';
import PricingModal from '../../pages/PricingModal'
import { useUser } from "@clerk/clerk-react"
import { getUserPlan } from '@/utils/plans';

const formatBreedName = (breedName) => {
  if (!breedName) return 'Tu Mascota';

  return breedName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Image Viewer Component
const ImageViewer = ({ isOpen, onClose, images, currentIndex = 0 }) => {
  const [index, setIndex] = useState(currentIndex);

  const handleNext = () => {
    setIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  useEffect(() => {
    setIndex(currentIndex);
  }, [currentIndex, isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!images || images.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal>
      <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw] p-0 bg-black/95 border-none max-h-[90vh] overflow-hidden">
        <div className="relative h-full flex flex-col">
          {/* Header with close button */}
          <div className="flex items-center justify-between p-3 bg-black/80 backdrop-blur-sm">
            <div className="text-white/80 text-sm">
              {index + 1} / {images.length}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="rounded-full h-8 w-8 text-white/80 hover:text-white hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Image container */}
          <div className="relative flex-1 flex items-center justify-center overflow-hidden">
            <div className="w-full h-full flex items-center justify-center p-4">
              <img
                src={images[index]}
                alt={`Image ${index + 1}`}
                className="max-w-full max-h-[calc(90vh-6rem)] object-contain select-none"
                draggable="false"
              />
            </div>
            
            {/* Navigation buttons */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full h-10 w-10 bg-black/30 text-white hover:bg-black/50 sm:left-4"
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full h-10 w-10 bg-black/30 text-white hover:bg-black/50 sm:right-4"
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}
          </div>
          
          {/* Thumbnail navigation for multiple images */}
          {images.length > 1 && (
            <div className="p-2 bg-black/80 backdrop-blur-sm overflow-x-auto">
              <div className="flex space-x-2 justify-center">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={`relative h-14 w-14 rounded-md overflow-hidden flex-shrink-0 transition-all ${
                      i === index ? 'ring-2 ring-amber-500 opacity-100' : 'opacity-60 hover:opacity-80'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${i + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Componente interno ShareCard
const ShareCard = ({ isOpen, onClose, imageUrls, nombreMascota, estadoGeneral, edad, coincidenciaRaza, razaPrincipal, confianza = 85 }) => {
  const [isSharing, setIsSharing] = useState(false);
  const [activeTab, setActiveTab] = useState("social");
  
  const handleShare = async (platform) => {
    setIsSharing(true);
    
    try {
      // URL de la plataforma productiva
      const platformUrl = "https://www.dogscanai.com/";
      
      // Mensaje más cálido y con CTA sutil
      const shareText = `¡Mira qué lindo es ${nombreMascota || 'mi mascota'}! 🐾 DogScan AI analizó su salud y ${estadoGeneral.toLowerCase()}. ¡Descubre cómo está tu mascota también!`;
      
      switch (platform) {
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + platformUrl)}`, '_blank');
          break;
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(platformUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
          break;
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(platformUrl)}`, '_blank');
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error sharing:', error);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden rounded-2xl border-0 shadow-xl">
        <div className="flex flex-col h-full bg-[#FFFDF6]">
          {/* Header con fondo crema original */}
          <div className="relative bg-[#FFF8E1] px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 rounded-full p-2 flex items-center justify-center">
                  <Dog className="h-5 w-5 text-amber-900" />
                </div>
                <div>
                  <DialogTitle className="text-lg font-bold text-amber-900">
                    Comparte a tu mascota
                  </DialogTitle>
                  <p className="text-xs text-amber-800 mt-0.5">
                    Muestra a tus amigos el análisis de tu mascota
                  </p>
                </div>
              </div>
              <DialogClose className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-black/5 transition-colors">
                <X className="h-4 w-4 text-gray-500" />
              </DialogClose>
            </div>
          </div>

          {/* Tabs originales */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab("social")}
              className={`flex-1 py-3 text-sm font-medium relative transition-all ${
                activeTab === "social" 
                  ? "text-amber-700" 
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Share2 className={`h-4 w-4 ${activeTab === "social" ? "text-amber-500" : ""}`} />
                <span>Redes sociales</span>
              </div>
              {activeTab === "social" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("preview")}
              className={`flex-1 py-3 text-sm font-medium relative transition-all ${
                activeTab === "preview" 
                  ? "text-amber-700" 
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Eye className={`h-4 w-4 ${activeTab === "preview" ? "text-amber-500" : ""}`} />
                <span>Vista previa</span>
              </div>
              {activeTab === "preview" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500" />
              )}
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto max-h-[70vh]">
            {activeTab === "social" ? (
              <div className="px-6 pb-6">
                {/* Tarjeta de compartir moderna estilo Canva */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg mb-6">
                  {/* Imagen principal */}
                  <div className="relative">
                    {imageUrls && imageUrls[0] ? (
                      <img
                        src={imageUrls[0]}
                        alt={nombreMascota || "Mascota"}
                        className="w-full object-cover"
                        style={{ height: '300px' }}
                      />
                    ) : (
                      <div className="w-full bg-gradient-to-r from-amber-100 to-amber-200 flex items-center justify-center" style={{ height: '300px' }}>
                        <span className="text-6xl">🐶</span>
                      </div>
                    )}
                    
                    {/* Badge de confianza */}
                    <div className="absolute top-4 right-4 bg-white rounded-full font-semibold text-amber-700 w-12 h-12 flex items-center justify-center shadow-lg text-lg">
                      {confianza}
                    </div>
                  </div>
                  
                  {/* Contenido */}
                  <div className="p-5">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                        {razaPrincipal || "West Highland White Terrier"}
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {edad || "3-5 años"}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {razaPrincipal || "West Highland White Terrier"}
                    </h3>
                    
                    {/* Estado de salud */}
                    <div className="bg-green-50 p-4 rounded-xl border border-green-100 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-green-100 mt-0.5">
                          <Heart className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-green-800 mb-1">Estado de Salud</h4>
                          <p className="text-green-700">
                            {estadoGeneral || "¡Tu perrito está espectacular! Muestra un excelente estado de salud, con un peso ideal y un pelaje que refleja buenos cuidados"}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <div className="text-center text-amber-700 bg-amber-50 p-3 rounded-xl font-medium">
                      ¡Descubre cómo está tu mascota con DogScan AI!
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="bg-amber-100 rounded-full p-1 flex items-center justify-center">
                        <Dog className="h-3 w-3 text-amber-900" />
                      </div>
                      <span className="text-sm text-gray-600">www.dogscanai.com</span>
                    </div>
                  </div>
                </div>
                
                {/* Botones de compartir modernos */}
                <h3 className="text-base font-semibold text-gray-800 mb-3">Compartir en redes sociales</h3>
                <div className="grid grid-cols-3 gap-3">
                  <Button
                    onClick={() => handleShare('whatsapp')}
                    disabled={isSharing}
                    className="h-12 bg-[#25D366] hover:bg-[#128C7E] text-white border-0 transition-all shadow-sm hover:shadow-md rounded-xl"
                  >
                    <div className="flex items-center justify-center gap-2 w-full">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      <span className="font-medium">WhatsApp</span>
                    </div>
                  </Button>
                  
                  <Button
                    onClick={() => handleShare('facebook')}
                    disabled={isSharing}
                    className="h-12 bg-[#1877F2] hover:bg-[#166FE5] text-white border-0 transition-all shadow-sm hover:shadow-md rounded-xl"
                  >
                    <div className="flex items-center justify-center gap-2 w-full">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      <span className="font-medium">Facebook</span>
                    </div>
                  </Button>
                  
                  <Button
                    onClick={() => handleShare('twitter')}
                    disabled={isSharing}
                    className="h-12 bg-[#1DA1F2] hover:bg-[#1A8CD8] text-white border-0 transition-all shadow-sm hover:shadow-md rounded-xl"
                  >
                    <div className="flex items-center justify-center gap-2 w-full">
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085a4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                      <span className="font-medium">Twitter</span>
                    </div>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="px-6 pb-6">
                {/* Vista previa de compartir */}
                <div className="bg-[#FFF8E1] p-4 rounded-xl mb-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-amber-100 mt-0.5">
                      <Eye className="h-4 w-4 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="font-medium text-amber-800 mb-1">Vista previa de compartir</h3>
                      <p className="text-sm text-amber-700/90">
                        Así es como se verá tu publicación cuando la compartas en redes sociales.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Tarjeta de vista previa moderna */}
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                  {/* Header */}
                  <div className="bg-[#FFF8E1] px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="bg-amber-100 rounded-full p-1.5 flex items-center justify-center">
                        <Dog className="h-4 w-4 text-amber-900" />
                      </div>
                      <span className="text-sm font-medium text-amber-800">DogScan AI • Análisis Inteligente</span>
                    </div>
                    <div className="bg-white rounded-full font-semibold text-amber-700 w-8 h-8 flex items-center justify-center shadow-sm text-sm">
                      {confianza}
                    </div>
                  </div>
                  
                  {/* Imagen */}
                  <div className="relative">
                    {imageUrls && imageUrls[0] ? (
                      <img
                        src={imageUrls[0]}
                        alt={nombreMascota || "Mascota"}
                        className="w-full object-cover"
                        style={{ height: '300px' }}
                      />
                    ) : (
                      <div className="w-full bg-gradient-to-r from-amber-100 to-amber-200 flex items-center justify-center" style={{ height: '300px' }}>
                        <span className="text-6xl">🐶</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Contenido */}
                  <div className="p-5">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                        {razaPrincipal || "West Highland White Terrier"}
                      </span>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {edad || "3-5 años"}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {razaPrincipal || "West Highland White Terrier"}
                    </h3>
                    
                    {/* Estado de salud */}
                    <div className="bg-green-50 p-4 rounded-xl border border-green-100 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-green-100 mt-0.5">
                          <Heart className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-green-800 mb-1">Estado de Salud</h4>
                          <p className="text-green-700">
                            {estadoGeneral || "¡Tu perrito está espectacular! Muestra un excelente estado de salud, con un peso ideal y un pelaje que refleja buenos cuidados"}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <div className="text-center text-amber-700 bg-amber-50 p-3 rounded-xl font-medium">
                      ¡Descubre cómo está tu mascota con DogScan AI!
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="bg-amber-100 rounded-full p-1 flex items-center justify-center">
                        <Dog className="h-3 w-3 text-amber-900" />
                      </div>
                      <span className="text-sm text-gray-600">www.dogscanai.com</span>
                    </div>
                    <span className="text-sm text-gray-500">© {new Date().getFullYear()}</span>
                  </div>
                </div>
                
                {/* Share Now Button */}
                <Button 
                  onClick={() => setActiveTab("social")}
                  className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white mt-6 shadow-md hover:shadow-lg transition-all rounded-xl"
                >
                  <Share2 className="h-5 w-5 mr-2" />
                  <span className="font-semibold">Compartir ahora</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const checkPremiumStatus = (user) => {
  if (!user) return false;
  if (!user.unsafeMetadata) return false;
  return user.unsafeMetadata.isPremium === true;
};
// Componente principal PetInfoCard
const PetInfoCard = ({
  nombreMascota,
  coincidenciaRaza,
  estadoGeneral,
  edad,
  handleExportPDF,
  isExporting,
  imageUrls = [],
  petName,
  razaSecundaria,
  razaPrincipal,
  confianza = 85
}) => {

  const { isSignedIn, isLoaded, user } = useUser();
  if(!isLoaded){
    return null;
  }

  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showUpgradeModalReport, setShowUpgradeModalReport] = useState(false);
  const isPremium = checkPremiumStatus(user);
  const plan = getUserPlan(user);

  razaSecundaria = formatBreedName(razaSecundaria);
  razaPrincipal = formatBreedName(razaPrincipal);
  
  const isUrgent = estadoGeneral?.toLowerCase().includes('crítico') || 
                  estadoGeneral?.toLowerCase().includes('inmediata') ||
                  estadoGeneral?.toLowerCase().includes('urgente');

  // Función segura para manejar la descarga del PDF
  const handleDownloadPDF = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if(!isPremium){
      setShowUpgradeModalReport(true);
    }
    
    if (typeof handleExportPDF === 'function' && !isExporting) {
      try {
        handleExportPDF();
      } catch (error) {
        console.error('Error al descargar PDF:', error);
        alert('Hubo un problema al generar el PDF. Por favor, intenta nuevamente.');
      }
    } else {
      console.warn('La función handleExportPDF no está disponible o está en proceso');
    }
  }, [handleExportPDF, isExporting]);

  return (
    <div className="relative">
      <Card className="rounded-xl overflow-hidden border-0 bg-white">
        {/* Header */}
        <div className="relative bg-amber-50/50 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-amber-100/80 rounded-full p-2">
                <span className="text-lg">🐾</span>
              </div>
              <h2 className="text-base font-semibold text-amber-800">
                Datos de tu mascota
              </h2>
            </div>
            
            <div className="flex items-center gap-2">    
              {/* Botón de descarga con técnica de superposición */}
              <div className="relative inline-block">
                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  disabled={isExporting}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  aria-label="Descargar PDF"
                />
                <div 
                  className={`
                    flex items-center justify-center gap-2 px-3 h-8 rounded-full
                    ${isExporting ? 'bg-amber-50/50 opacity-70' : 'bg-white/80 hover:bg-amber-50'}
                    text-amber-700 shadow-sm border border-amber-200/50 transition-colors duration-200
                    ${isExporting ? 'cursor-not-allowed' : 'cursor-pointer'}
                    pointer-events-none
                  `}
                >
                  {isExporting ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin sm:mr-2" />
                      <span className="hidden sm:inline">Procesando...</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Descargar</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            {/* Left Column - Image */}
            <div className="relative flex-shrink-0 sm:w-56">
              <div 
                onClick={() => {
                  setCurrentImageIndex(0);
                  setIsImageViewerOpen(true);
                }}
                className="relative w-full aspect-square rounded-xl overflow-hidden shadow-md cursor-pointer group"
              >
                {imageUrls && imageUrls[0] ? (
                  <>
                    <img
                      src={imageUrls[0]}
                      alt={petName || razaPrincipal || "Mascota"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center transition-colors">
                      <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                    <span className="text-4xl">🐶</span>
                  </div>
                )}

                {/* Confidence Badge */}
                <div className="absolute -top-1 -right-1 bg-white text-amber-700 px-2 py-1 rounded-full shadow-md border border-amber-200/50 flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-medium">{confianza}%</span>
                </div>

                {/* Multiple Images Badge */}
                {imageUrls?.length > 1 && (
                  <button 
                    onClick={() => setIsImageViewerOpen(true)}
                    className="absolute -bottom-1 -right-1 bg-white text-amber-700 px-2 py-1 rounded-full shadow-md border border-amber-200/50 flex items-center gap-1"
                  >
                    <Camera className="h-3.5 w-3.5" />
                    <span className="text-sm">{imageUrls.length}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Right Column - Information */}
            <div className="flex-1 min-w-0">
              <div className="space-y-4">
                {/* Breed Information */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-800">
                      {razaPrincipal}
                    </h3>
                    <div className="bg-amber-100/60 text-amber-700 px-2 py-0.5 rounded-full text-sm">
                      {confianza}% de precisión
                    </div>
                  </div>
                  
                  {razaSecundaria && razaSecundaria !== 'Null' && razaPrincipal !== razaSecundaria && (
                    <div className="inline-block px-3 py-1 bg-amber-50 text-amber-700 rounded-full text-sm mb-3">
                      Rasgos de {razaSecundaria}
                    </div>
                  )}
                  
                  {/* Age Information */}
                  <div className="bg-amber-50 px-3 py-2 rounded-lg text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-500" />
                      <span>
                        Edad aproximada:{' '}
                        <span className="font-medium text-amber-700">{edad}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Health Status */}
                <div className={`p-3 rounded-lg ${
                  isUrgent 
                    ? 'bg-red-50 border border-red-100'
                    : 'bg-emerald-50 border border-emerald-100'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      isUrgent
                        ? 'bg-red-100'
                        : 'bg-emerald-100'
                    }`}>
                      <Heart className={`h-5 w-5 ${
                        isUrgent
                          ? 'text-red-500'
                          : 'text-emerald-500'
                      }`} />
                    </div>
                    <div>
                      <h4 className={`font-medium mb-1 ${
                        isUrgent ? 'text-red-700' : 'text-emerald-700'
                      }`}>
                        Estado de Salud
                      </h4>
                      <p className={`text-sm leading-relaxed ${
                        isUrgent ? 'text-red-600' : 'text-emerald-600'
                      }`}>
                        {estadoGeneral}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Image Viewer */}
      <ImageViewer
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
        images={imageUrls}
        currentIndex={currentImageIndex}
      />

      {/* Share Card */}
      {/* <ShareCard
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        imageUrls={imageUrls}
        nombreMascota={nombreMascota || petName}
        estadoGeneral={estadoGeneral}
        edad={edad}
        coincidenciaRaza={coincidenciaRaza}
        razaPrincipal={razaPrincipal}
        confianza={confianza}
      /> */}

       {/* Modal */}
    

    </div>
  );
};

export default PetInfoCard;